import React, { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import InnerShadowBox from "components/Shared/InnerShadowBox";
import RichTextEditor from "components/Shared/RichTextEditor";
import { useAuth } from "hooks";
import { useLazySearchUsersQuery } from "store/services/userApi";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

const INITIAL_STATE = {
  content: "",
  type: "Note",
  showClient: false,
  linkToShipments: false,
  responsible: null,
};

const MODULES_WITH_SHIPMENTS = [
  "Collection",
  "Consolidation",
  "ShipmentLoading",
  "ShipmentUnLoading",
  "Deconsolidation",
  "Warehousing",
  "SendOut",
  "SendOutCollection",
  "Delivery",
];

const BasicNoteForm = ({
  noteDetail,
  isShowClient = false,
  isBusy = false,
  noteTypes = ["Note", "Call", "Meeting", "Email", "Other"],
  source,
  showResponsible = false,
  onSubmit = () => { },
  onCancel = () => { },
}) => {
  const { isVendor, isCustomer, isDriver, isContractor } = useAuth();
  const searchTimerRef = useRef(null);

  const [searchUsers, { data: userResults, isLoading: searchingUsers }] =
    useLazySearchUsersQuery();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      content: noteDetail?.content ?? "",
      type: noteDetail?.type ?? "Note",
      showClient:
        noteDetail?.showClient ??
        (source === "Shipment" ||
          isCustomer ||
          isVendor ||
          isDriver ||
          isContractor),
      linkToShipments: noteDetail?.linkToShipments ?? false,
      responsible: noteDetail?.responsible ?? null,
    },
  });

  React.useEffect(() => {
    if (!noteDetail) {
      reset({
        ...INITIAL_STATE,
        showClient:
          source === "Shipment" ||
          isCustomer ||
          isVendor ||
          isDriver ||
          isContractor,
        linkToShipments: false,
        responsible: null,
      });
      return;
    }

    reset({
      ...noteDetail,
      showClient:
        isCustomer || isVendor || isDriver || isContractor
          ? true
          : noteDetail?.showClient ?? source === "Shipment",
      linkToShipments: noteDetail?.linkToShipments ?? false,
      responsible: noteDetail?.responsible ?? null,
    });
  }, [noteDetail, reset, isCustomer, isVendor, isDriver, isContractor, source]);

  return (
    <LoadingWrapper loading={isBusy}>
      <InnerShadowBox padding={3}>
        <Grid container spacing={5}>
          <Grid item md={12}>
            <Controller
              name="content"
              control={control}
              rules={{ required: "Please provide the note Content." }}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={
                    noteDetail?.content
                      ? noteDetail.content
                      : "<p>Enter note here.</p>"
                  }
                  onChange={onChange}
                  height={300}
                />
              )}
            />
            {!!errors.content && (
              <FormHelperText error>{errors.content.message}</FormHelperText>
            )}
          </Grid>

          {showResponsible && (
            <Grid item md={12}>
              <Controller
                name="responsible"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Responsible"
                    value={value ?? null}
                    onChange={(_, data) => onChange(data)}
                    onInputChange={(_, val) => {
                      clearTimeout(searchTimerRef.current);
                      searchTimerRef.current = setTimeout(() => {
                        if (val) searchUsers({ q: val });
                      }, 300);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={userResults || []}
                    loading={searchingUsers}
                    filterOptions={(x) => x}
                  />
                )}
              />
            </Grid>
          )}

          {!isShowClient &&
            !isCustomer &&
            !isVendor &&
            !isContractor &&
            !isDriver &&
            source !== "IncidentReport" && (
              <Grid item md={12}>
                <Controller
                  name="showClient"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="showClient"
                          color="primary"
                          checked={field.value}
                          size="small"
                          {...field}
                        />
                      }
                      label="Show to Client"
                    />
                  )}
                />
              </Grid>
            )}

          {MODULES_WITH_SHIPMENTS.includes(source) &&
            !isCustomer &&
            !isVendor &&
            !isContractor &&
            !isDriver && (
              <Grid item md={12}>
                <Controller
                  name="linkToShipments"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="linkToShipments"
                          color="primary"
                          checked={field.value}
                          size="small"
                          {...field}
                        />
                      }
                      label="Show in Shipments"
                    />
                  )}
                />
              </Grid>
            )}

          <Grid item md={12}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="select-note">Type</InputLabel>
                  <Select
                    fullWidth
                    labelId="select-note"
                    label="Type"
                    placeholder="Select Note Type"
                    size="small"
                    id="role"
                    value={field.value || "Note"}
                    {...field}
                  >
                    {noteTypes?.map((note) => (
                      <MenuItem key={note} value={note}>
                        {note}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </InnerShadowBox>

      <Divider />

      <Box
        paddingX={3}
        paddingY={2}
        gap={2}
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          disabled={isBusy}
          onClick={() => {
            onCancel();
            reset(INITIAL_STATE);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          disabled={isBusy}
          onClick={handleSubmit((data) => {
            const finalData = {
              ...data,
              showClient:
                isCustomer || isVendor || isDriver || isContractor
                  ? true
                  : data.showClient,
              responsible: data.responsible?._id ?? null,
            };
            onSubmit(finalData);
          })}
        >
          {noteDetail ? "Update" : "Add"}
        </Button>
      </Box>
    </LoadingWrapper>
  );
};

export default BasicNoteForm;